import { useMemo, useState } from 'react';

import { ALL_EVALUATORS } from '../components/SkillsetAssessment/skillsetPeriodIds';
import {
    describeSkillsetPeriod,
    openingSkillsetPeriod,
    toScoreWindow,
} from '../components/SkillsetAssessment/skillsetWindow';
import { useSkillsetEvaluationPeriods } from '../components/SkillsetAssessment/useSkillsetEvaluationPeriods';

import type { SkillsetPeriod } from '../components/SkillsetAssessment/skillsetPeriodIds';

import { useGetPerformanceScore } from '@/api/performance-profile';
import i18n from '@/plugins/i18n';

/** What the competency filters hold: who assessed, and when. */
export interface SkillsetFilter {
    evaluatorId: string;
    period: SkillsetPeriod;
}

/**
 * The employee's performance score, and the competency filters it is read through.
 *
 * The filters belong to the page, not to the list they narrow: the score is the average of those
 * very competencies, and the score card sits above the list. Held inside the list, the two would
 * answer different questions side by side. Only the reader's own choice is remembered: the lens the
 * profile opens on is `openingSkillsetPeriod`, so it is never a stale copy of a rule written
 * elsewhere.
 *
 * `employeeId` absent, nothing is asked of the server.
 */
export const useProfileScore = (employeeId: string | undefined) => {
    const [pickedSkillsetFilter, setPickedSkillsetFilter] = useState<SkillsetFilter | null>(null);
    // The periods this employee was evaluated over, not the ones a cycle may be set to: built from
    // the setup vocabulary, the filter offers periods the data may not hold, and the page then
    // empties without saying that a filter emptied it. Both tabs show the same score, so both name
    // the same period; only one of them carries the filter that set it.
    const { periods, arePeriodsLoading } = useSkillsetEvaluationPeriods(employeeId);
    const skillsetFilter = useMemo<SkillsetFilter>(
        () =>
            pickedSkillsetFilter ?? {
                evaluatorId: ALL_EVALUATORS,
                period: openingSkillsetPeriod(),
            },
        [pickedSkillsetFilter],
    );
    const scoreWindow = useMemo(
        () => toScoreWindow(skillsetFilter.period, skillsetFilter.evaluatorId),
        [skillsetFilter],
    );
    // The app's own instance, as `SkillsetAssessmentFilters` reads it beside the page: the hook's
    // `i18n` is absent from more than one hand-written mock, and a date range is not worth a
    // component that renders differently under test than on screen.
    const scorePeriodLabel = describeSkillsetPeriod(skillsetFilter.period, periods, i18n.language);

    const {
        data: score,
        isLoading,
        error: scoreError,
        isPlaceholderData: isScoreStale,
    } = useGetPerformanceScore(employeeId, scoreWindow);

    return {
        score,
        /**
         * The periods are part of the answer, not a detail of it: until they land, the period picker
         * would offer every period alone, as if the employee had never been evaluated over one.
         */
        isScoreLoading: arePeriodsLoading || isLoading,
        scoreError,
        /** True while what is on screen answers the previous lens, not the one now picked. */
        isScoreStale,
        skillsetFilter,
        onSkillsetFilterChange: setPickedSkillsetFilter,
        scorePeriodLabel,
    };
};
